/**
 * Portfolio validation utility functions
 * Extracted from StampFromPortfolio.js to be reused across components
 */

// Common regex patterns for portfolio validation
export const DELIMITER_REGEX = /[\t,; ]+/; // tab, comma, semicolon, or one/more spaces (kept for compatibility)
export const SYMBOL_REGEX = /^[a-zA-Z0-9.-]+$/; // alphanumeric with "." and "-" separators

/**
 * Cleans HTML content by removing tags and converting to plain text
 * @param {string} content - HTML content to clean
 * @returns {string} - Plain text content
 */
export const cleanHtmlContent = (content) => {
  return content
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<p[^>]*>/gi, "")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .trim();
};

/**
 * Symbol column synonyms - various names for ticker/symbol column
 */
const SYMBOL_SYNONYMS = [
  "id",
  "id_ticker",
  "id_symbol",
  "identifier",
  "sym",
  "symbol",
  "ticker",
  "ticker_symbol",
  "security",
  "asset",
  "instrument",
  "stock ticker",
  "figi",
  "isin",
  "bbgid",
  "bbg_ticker",
  "bloomberg_id",
];

/**
 * Weight column synonyms - various names for weight/allocation column
 */
const WEIGHT_SYNONYMS = [
  "wt",
  "w",
  "weight",
  "weight_pct",
  "weight_percent",
  "weighting",
  "percentage",
  "pct",
  "allocation",
  "exposure",
  "position",
  "% of book",
  "portfolio_weight",
  "allocation_pct",
  "allocation_percent",
];

/**
 * Detects if a header row contains symbol and weight column synonyms
 * @param {Array<string>} headers - Array of lowercase header names
 * @returns {Object} { hasSymbol: boolean, hasWeight: boolean, symbolIndex: number, weightIndex: number }
 */
const detectHeaderSynonyms = (headers) => {
  let symbolIndex = -1;
  let weightIndex = -1;

  headers.forEach((header, idx) => {
    // Normalize header: remove quotes, extra spaces
    const cleaned = header
      .trim()
      .toLowerCase()
      .replace(/["']/g, "")
      .replace(/\s+/g, " ");

    // Create both versions: with underscore and with space
    const withUnderscore = cleaned.replace(/\s+/g, "_");
    const withSpace = cleaned.replace(/_/g, " ");

    // Check if it matches any symbol synonym (try both versions)
    if (symbolIndex === -1) {
      if (
        SYMBOL_SYNONYMS.includes(cleaned) ||
        SYMBOL_SYNONYMS.includes(withUnderscore) ||
        SYMBOL_SYNONYMS.includes(withSpace)
      ) {
        symbolIndex = idx;
      }
    }

    // Check if it matches any weight synonym (try both versions)
    if (weightIndex === -1) {
      if (
        WEIGHT_SYNONYMS.includes(cleaned) ||
        WEIGHT_SYNONYMS.includes(withUnderscore) ||
        WEIGHT_SYNONYMS.includes(withSpace)
      ) {
        weightIndex = idx;
      }
    }
  });

  return {
    hasSymbol: symbolIndex !== -1,
    hasWeight: weightIndex !== -1,
    symbolIndex,
    weightIndex,
  };
};

/**
 * Normalizes portfolio content by converting all delimiters to commas
 * Always ensures header row (sym,wt) is present
 * Handles custom headers with synonym detection
 * @param {string} text - The portfolio text to normalize
 * @returns {string} - Normalized portfolio content with comma delimiters and standard sym,wt header
 */
export const normalizePortfolioContent = (text) => {
  if (!text || !text.trim()) {
    return "";
  }

  const rawLines = text
    .trim()
    .split("\n")
    .filter((line) => line.trim());

  if (rawLines.length === 0) {
    return "";
  }

  // Detect decimal format for the entire file
  const decimalFormatCheck = detectDecimalFormat(text);
  const decimalFormat = decimalFormatCheck.format; // "comma", "dot", or null

  // Check if first line is a header using synonym detection
  const firstLineColumns = smartParseLine(rawLines[0]).map((col) =>
    col.trim().toLowerCase(),
  );

  const headerDetection = detectHeaderSynonyms(firstLineColumns);
  let dataStartRow = 0;
  let columnMapping = null;

  if (headerDetection.hasSymbol && headerDetection.hasWeight) {
    // Header detected with synonyms
    dataStartRow = 1;
    columnMapping = {
      symbolIndex: headerDetection.symbolIndex,
      weightIndex: headerDetection.weightIndex,
    };
  }

  // Process data rows
  const normalizedRows = [];
  for (let i = dataStartRow; i < rawLines.length; i++) {
    const columns = smartParseLine(rawLines[i]);

    if (columns.length === 0) continue;

    let symbol, weight;

    if (columnMapping) {
      // Extract from custom column positions
      if (
        columns.length >
        Math.max(columnMapping.symbolIndex, columnMapping.weightIndex)
      ) {
        symbol = columns[columnMapping.symbolIndex];
        weight = columns[columnMapping.weightIndex];
      } else {
        continue; // Skip malformed rows
      }
    } else {
      // Standard 2-column format
      if (columns.length >= 2) {
        [symbol, weight] = columns;
      } else {
        continue; // Skip malformed rows
      }
    }

    if (symbol && weight) {
      // Process weight to normalize format (European comma → US dot, percentages, etc.)
      // Pass decimal format from file-level detection to processWeight
      const weightResult = processWeight(weight, decimalFormat);
      if (weightResult.valid) {
        normalizedRows.push(`${symbol},${weightResult.value}`);
      }
      // Skip invalid weights (they'll be caught in validation)
    }
  }

  // Always use standard header
  normalizedRows.unshift("sym,wt");

  return normalizedRows.join("\n");
};

/**
 * Smart parsing of a line that may contain European decimals
 * Handles cases like "AAPL,0,25" or "ddd,(0,25)" where commas are used as both delimiter and decimal
 * @param {string} line - The line to parse
 * @returns {Array<string>} - Array of columns [symbol, weight]
 */
const smartParseLine = (line) => {
  if (!line || !line.trim()) return [];

  // Clean up: remove spaces around commas (e.g., "ddd, (0,25)" → "ddd,(0,25)")
  const trimmed = line.trim().replace(/\s*,\s*/g, ",");

  // Try non-comma delimiters first (clearer separation)
  if (trimmed.includes(";")) {
    return trimmed
      .split(/;+/)
      .map((c) => c.trim())
      .filter((c) => c);
  }
  if (trimmed.includes("\t")) {
    return trimmed
      .split(/\t+/)
      .map((c) => c.trim())
      .filter((c) => c);
  }

  // Check for space delimiter
  // Pattern: non-space-non-comma word, then space(s), then rest
  const spaceMatch = trimmed.match(/^([^\s,]+)\s+(.+)$/);
  if (spaceMatch) {
    // Allow space delimiter if:
    // 1. No comma at all (clean space-delimited)
    // 2. OR comma only appears in number patterns like "(0,25)" (European decimal)
    const hasNonDecimalComma =
      trimmed.includes(",") && !/^[^\s,]+\s+\(?\d+,\d+\)?/.test(trimmed);

    if (!hasNonDecimalComma) {
      return [spaceMatch[1].trim(), spaceMatch[2].trim()];
    }
  }

  // Now handle comma-delimited lines
  // Need to distinguish between column comma and decimal comma

  // Strategy: Split by ALL commas, then intelligently merge
  const parts = trimmed
    .split(",")
    .map((c) => c.trim())
    .filter((c) => c);

  if (parts.length === 2) {
    // Simple case: just 2 parts
    return parts;
  }

  if (parts.length === 3) {
    // Could be: "SYM,0,25" or "SYM,(0,25)" or "SYM,0,25%"
    // Check if parts[1] and parts[2] form a European decimal or negative number
    const candidate = parts[1] + "," + parts[2];

    // Pattern for European decimal (with or without parentheses, percentage, minus)
    // Matches: "0,25", "(0,25)", "-0,25", "0,25%", "(0,004888...)"
    // Ensures parentheses are properly paired: \(? matches opening, \)? matches closing
    // Allow any number of digits after comma for precision
    if (/^\(?-?\d+,\d+\)?%?$/.test(candidate)) {
      return [parts[0], candidate];
    }
  }

  if (parts.length === 4) {
    // Could be: "SYM,(0,25)"
    // parts = ["SYM", "(0", "25)"]... wait, that's 3 parts
    // Or: "SYM,(0,25" split incorrectly

    // Try different merging strategies
    // Merge last 3: "(0,25)"
    const lastThree = parts[1] + "," + parts[2] + "," + parts[3];
    if (/^\(?\d+,\d+,\d+\)?$/.test(lastThree)) {
      return [parts[0], lastThree];
    }

    // Merge last 2: "0,25"
    const lastTwo = parts[2] + "," + parts[3];
    // Check if lastTwo is European decimal pattern using shared logic
    const lastTwoParts = lastTwo.split(",");
    if (
      lastTwoParts.length === 2 &&
      /^\d+$/.test(lastTwoParts[0]) &&
      /^\d+$/.test(lastTwoParts[1]) &&
      isEuropeanDecimalPattern(lastTwoParts[1])
    ) {
      // Merge parts[1] with lastTwo if it has opening paren
      if (parts[1].startsWith("(")) {
        return [parts[0], parts[1] + "," + lastTwo];
      }
    }
  }

  // Fallback: return all parts (will likely fail validation)
  return parts;
};

/**
 * Helper function: Check if digits after comma indicate European decimal format
 * @param {string} digitsAfterComma - Digits after the comma
 * @returns {boolean} - True if European decimal, false if US thousands
 *
 * Logic:
 * - European decimals: 1-4 digits after comma (e.g., "0,25", "1,2345")
 * - US thousands: exactly 3 digits after comma (e.g., "1,234")
 * - Exception: 3 digits is treated as US thousands due to standard formatting
 */
const isEuropeanDecimalPattern = (digitsAfterComma) => {
  const length = digitsAfterComma.length;
  return length >= 1 && length <= 4 && length !== 3;
};

/**
 * Detects the decimal format used in portfolio content
 * @param {string} content - Full portfolio CSV content
 * @returns {Object} { valid: boolean, format: 'comma'|'dot'|null, message?: string }
 */
const detectDecimalFormat = (content) => {
  if (!content || !content.trim()) {
    return { valid: true, format: null };
  }

  // Look for patterns that indicate decimal separators
  // European decimal: "0,25" "1,5" "0,004888..." (comma with digits after)
  // US decimal: "0.25" "1.5" "0.004888..." (dot with digits after)

  const europeanDecimalPattern = /\d+,\d+/g; // Digits, comma, digits
  const usDecimalPattern = /\d+\.\d+/g; // Digits, dot, digits

  const hasEuropeanDecimals = europeanDecimalPattern.test(content);
  const usDecimals = content.match(usDecimalPattern);

  // Filter out potential thousands separators (like "1.234" in "1.234.567")
  const hasUSDecimals =
    usDecimals &&
    usDecimals.some((match) => {
      // Check if this is not part of a European thousands separator pattern
      const index = content.indexOf(match);
      const afterMatch = content.substring(
        index + match.length,
        index + match.length + 5,
      );
      return !afterMatch.match(/^[.,]\d/); // Not followed by another separator
    });

  if (hasEuropeanDecimals && hasUSDecimals) {
    return {
      valid: false,
      format: null,
      message:
        "Mixed decimal formats detected. Please use either comma (European) or dot (US) consistently throughout the file.",
    };
  }

  if (hasEuropeanDecimals) {
    return { valid: true, format: "comma" };
  }

  if (hasUSDecimals) {
    return { valid: true, format: "dot" };
  }

  // No decimals detected - could be all integers
  return { valid: true, format: null };
};

/**
 * Processes and converts various weight formats to normalized decimal number
 *
 * Supported formats:
 * - Standard: 0.5, -0.5, 1.234
 * - Percentage: 25%, -2.5%, 0.35%
 * - European decimal: 0,25 (comma as decimal separator)
 * - Parentheses (negative): (0.5), (25%), (0,25)
 * - Combined: (2.5%), (0,5%)
 * - US thousands: 1,234.56
 * - European thousands: 1.234,56
 *
 * @param {string} weightStr - Raw weight string from CSV
 * @param {string|null} decimalFormat - Format of the file: "comma" (European), "dot" (US), or null (auto-detect)
 * @returns {Object} { valid: boolean, value?: number, error?: string }
 *
 * @example
 * processWeight("0.5")      → { valid: true, value: 0.5 }
 * processWeight("25%")      → { valid: true, value: 0.25 }
 * processWeight("0,25")     → { valid: true, value: 0.25 }
 * processWeight("1,200", "comma") → { valid: true, value: 1.2 } (European decimal)
 * processWeight("1,200")   → { valid: true, value: 1200 } (US thousands, auto-detect)
 * processWeight("(0.5)")    → { valid: true, value: -0.5 }
 * processWeight("(25%)")    → { valid: true, value: -0.25 }
 * processWeight("1,234.56") → { valid: true, value: 1234.56 }
 * processWeight("abc")      → { valid: false, error: "..." }
 */
export const processWeight = (weightStr, decimalFormat = null) => {
  // Handle empty/null/undefined
  if (weightStr === null || weightStr === undefined || weightStr === "") {
    return { valid: false, error: "Weight is empty" };
  }

  if (typeof weightStr !== "string") {
    weightStr = String(weightStr);
  }

  let cleaned = weightStr.trim();

  // Step 1: Detect and remove parentheses (indicates negative)
  const isNegative = /^\(.*\)$/.test(cleaned);
  if (isNegative) {
    cleaned = cleaned.replace(/[()]/g, "").trim();
  }

  // Step 2: Detect and remove percentage sign
  const isPercent = cleaned.includes("%");
  if (isPercent) {
    cleaned = cleaned.replace(/%/g, "").trim();
  }

  // Step 3: Detect decimal format (European vs US)
  // We need to distinguish between:
  // - European decimal: "0,25" or "1.234,56" (comma is decimal)
  // - US decimal: "0.25" or "1,234.56" (dot is decimal)
  // - US thousands: "1,234" (comma is thousands separator)

  const hasComma = cleaned.includes(",");
  const hasDot = cleaned.includes(".");

  if (hasComma && hasDot) {
    // Both separators present - determine which is decimal
    const lastCommaIdx = cleaned.lastIndexOf(",");
    const lastDotIdx = cleaned.lastIndexOf(".");

    if (lastCommaIdx > lastDotIdx) {
      // European format: "1.234,56" → comma is decimal
      cleaned = cleaned.replace(/\./g, "").replace(",", ".");
    } else {
      // US format: "1,234.56" → dot is decimal
      cleaned = cleaned.replace(/,/g, "");
    }
  } else if (hasComma && !hasDot) {
    // Only comma present - could be European decimal "0,25" or US thousands "1,234"
    const commaCount = (cleaned.match(/,/g) || []).length;
    const parts = cleaned.split(",");
    const beforeComma = parts[0];
    const afterComma = parts[parts.length - 1];

    // If decimalFormat is explicitly "comma", ALL commas are decimal separators
    // Per TZ: "comma (',') is used as the decimal separator and a period ('.') may be used as the thousands separator"
    // This means: in European format, comma = decimal, period = thousands
    // Examples: "0,02" → 0.02, "1,200" → 1.2, "10,902" → 10.902
    if (decimalFormat === "comma") {
      // European format: ALL commas are decimal separators (per TZ requirement)
      // "0,02" → 0.02, "1,200" → 1.2, "10,902" → 10.902, "900,2" → 900.2
      cleaned = cleaned.replace(",", ".");
    } else {
      // Auto-detect: distinguish European decimal from US thousands
      // If number starts with 0 (or is just 0), comma is always a decimal separator
      // Examples: "0,202" → 0.202 (decimal), "0,2" → 0.2 (decimal)
      // If number is >= 1 and has exactly 3 digits after comma, it's likely thousands
      // Examples: "1,234" → 1234 (thousands), "12,345" → 12345 (thousands)
      const startsWithZero = /^0+$/.test(beforeComma) || beforeComma === "";
      const hasThreeDigitsAfterComma = afterComma.length === 3;

      if (startsWithZero) {
        // Always treat as European decimal if starts with 0
        // "0,202" → 0.202, "0,2" → 0.2
        cleaned = cleaned.replace(",", ".");
      } else if (commaCount === 1 && isEuropeanDecimalPattern(afterComma)) {
        // European decimal: "1,5" or "1,2345" (not 3 digits)
        cleaned = cleaned.replace(",", ".");
      } else if (hasThreeDigitsAfterComma && commaCount === 1) {
        // US thousands: "1,234" or "12,345" (exactly 3 digits, number >= 1)
        cleaned = cleaned.replace(/,/g, "");
      } else {
        // Multiple commas or other cases - treat as thousands
        cleaned = cleaned.replace(/,/g, "");
      }
    }
  } else if (!hasComma && hasDot) {
    // Only dot present - could be US decimal "0.25" or European thousands "1.234"
    const dotCount = (cleaned.match(/\./g) || []).length;

    if (dotCount > 1) {
      // Multiple dots → European thousands: "1.234.567"
      cleaned = cleaned.replace(/\./g, "");
    }
    // Single dot → US decimal "0.25" - keep as is
  }

  // Step 4: Handle leading dot (e.g., ".25" → "0.25")
  if (cleaned.startsWith(".")) {
    cleaned = "0" + cleaned;
  } else if (cleaned.startsWith("-.")) {
    cleaned = "-0" + cleaned.substring(1);
  }

  // Step 5: Remove any remaining spaces
  cleaned = cleaned.replace(/\s+/g, "");

  // Step 6: Validate that cleaned string contains only valid numeric characters
  // Reject strings with letters or other invalid characters (except scientific notation)
  const validNumericPattern = /^-?\d+\.?\d*([eE][+-]?\d+)?$/;
  if (!validNumericPattern.test(cleaned)) {
    return {
      valid: false,
      error: `Cannot parse "${weightStr}" as a valid number`,
    };
  }

  // Step 7: Parse as float
  let value = parseFloat(cleaned);

  // Step 8: Validate result
  if (isNaN(value) || !isFinite(value)) {
    return {
      valid: false,
      error: `Cannot parse "${weightStr}" as a valid number`,
    };
  }

  // Step 9: Apply percentage conversion
  if (isPercent) {
    value = value / 100;
  }

  // Step 10: Apply negative sign from parentheses
  if (isNegative) {
    value = -Math.abs(value);
  }

  // Step 11: Round to 10 decimal places to avoid floating point precision errors
  // This handles typical financial precision requirements while eliminating JavaScript
  // floating point errors (e.g., 0.35/100 = 0.0034999999999999996 → 0.0035)
  // 10 decimals is sufficient for portfolio weights which typically need 4-8 decimal precision
  value = Math.round(value * 1e10) / 1e10;

  // Step 12: Normalize -0 to 0 (JavaScript quirk)
  if (Object.is(value, -0)) {
    value = 0;
  }

  return { valid: true, value };
};

/**
 * Validates portfolio data in CSV format
 * @param {string} content - The content to validate (CSV text)
 * @returns {Object} - Validation result with isValid boolean and message string
 */
export const validatePortfolioContent = (content) => {
  if (!content || !content.trim()) {
    return {
      isValid: false,
      message: "Portfolio content is required for portfolio collections.",
    };
  }

  // Check decimal format consistency (European comma vs US dot)
  const decimalFormatCheck = detectDecimalFormat(content);
  if (!decimalFormatCheck.valid) {
    return {
      isValid: false,
      message: decimalFormatCheck.message,
    };
  }

  const lines = content
    .trim()
    .split("\n")
    .filter((line) => line.trim());

  // Check if we have at least one data row
  if (lines.length < 1) {
    return {
      isValid: false,
      message: "Portfolio must have at least one row of data.",
    };
  }

  // Determine starting row: skip header if first row looks like header
  let startRow = 0;
  let columnMapping = null; // Will store { symbolIndex, weightIndex } if custom headers detected

  const firstLine = smartParseLine(lines[0]).map((col) =>
    col.trim().toLowerCase(),
  );

  // Check if first row is a header using synonym detection
  const headerDetection = detectHeaderSynonyms(firstLine);

  // Check for partial header match (one column recognized, but not both)
  if (
    (headerDetection.hasSymbol && !headerDetection.hasWeight) ||
    (!headerDetection.hasSymbol && headerDetection.hasWeight)
  ) {
    const missingColumn = headerDetection.hasSymbol ? "weight" : "symbol";
    const recognizedColumn = headerDetection.hasSymbol ? "symbol" : "weight";
    return {
      isValid: false,
      message: `Unrecognized header: The first row appears to be a header (${recognizedColumn} column detected), but the ${missingColumn} column name is not recognized.`,
    };
  }

  if (headerDetection.hasSymbol && headerDetection.hasWeight) {
    startRow = 1; // Skip header row
    columnMapping = {
      symbolIndex: headerDetection.symbolIndex,
      weightIndex: headerDetection.weightIndex,
    };

    if (lines.length < 2) {
      return {
        isValid: false,
        message: "Portfolio must have at least one row of data after header.",
      };
    }
  }

  // Check delimiter consistency between header and data rows
  // Note: For European decimals (AAPL,0,25), the comma in "0,25" is NOT a delimiter
  let primaryDelimiter = null;
  const delimiterNames = {
    ",": "comma",
    ";": "semicolon",
    "\t": "tab",
    " ": "space",
  };

  // Detect column delimiter (not decimal comma)
  const detectColumnDelimiter = (line) => {
    // Clean up spaces around commas first (same as smartParseLine)
    const cleaned = line.replace(/\s*,\s*/g, ",");

    // For European decimals, ignore commas that are part of numbers
    if (decimalFormatCheck.format === "comma") {
      // Check for semicolon, tab first
      if (cleaned.includes(";")) return ";";
      if (cleaned.includes("\t")) return "\t";

      // Remove number patterns like "0,25" or "(0,25)" before checking delimiters
      const withoutNumbers = cleaned.replace(/\(?\d+,\d+\)?/g, "PLACEHOLDER");

      // Check for space (after removing number patterns)
      if (withoutNumbers.includes(" ")) {
        return " ";
      }

      // Check for comma (that's not part of a number)
      // After replacing numbers with PLACEHOLDER, any remaining comma is a delimiter
      if (withoutNumbers.includes(",")) {
        return ",";
      }

      return null;
    } else {
      // Standard delimiter detection
      if (cleaned.includes(";")) return ";";
      if (cleaned.includes("\t")) return "\t";
      if (cleaned.includes(",")) return ",";
      if (cleaned.includes(" ")) return " ";
      return null;
    }
  };

  // If header exists, detect its delimiter first
  if (startRow > 0) {
    const headerLine = lines[0].trim();
    primaryDelimiter = detectColumnDelimiter(headerLine);
  }

  // Check data rows for delimiter consistency
  for (let i = startRow; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const delimiter = detectColumnDelimiter(line);
    if (delimiter) {
      if (primaryDelimiter === null) {
        primaryDelimiter = delimiter;
      } else if (delimiter !== primaryDelimiter) {
        return {
          isValid: false,
          message: `Inconsistent delimiters: header uses ${
            delimiterNames[primaryDelimiter]
          }, but row ${i + 1} uses ${
            delimiterNames[delimiter]
          }. Please use the same delimiter throughout.`,
        };
      }
    }
  }

  // Validate data rows using flexible delimiters
  let hasValidData = false;
  for (let i = startRow; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue; // Skip empty lines

    // Check for consecutive delimiters (empty fields between delimiters)
    // Examples: "ddd,,33" or "ddd;;33" or "ddd  33" (multiple spaces between columns)
    // This indicates malformed data - empty field between delimiters
    // Note: For spaces, only check if they're between two non-space tokens (actual column delimiters)
    // Only check multiple spaces if space is the primary delimiter to avoid false positives
    // (e.g., "AAPL INC" is valid if delimiter is comma, but invalid if delimiter is space)
    const hasConsecutiveDelimiters =
      /[,;]\s*[,;]/.test(line) || // Consecutive commas or semicolons
      /\t\s*\t/.test(line) || // Consecutive tabs
      (primaryDelimiter === " " && /[^\s]\s{2,}[^\s]/.test(line)); // Multiple consecutive spaces between non-space characters (column delimiters), only if delimiter is space

    if (hasConsecutiveDelimiters) {
      return {
        isValid: false,
        message: `Row ${
          i + 1
        }: Empty field detected between delimiters. Please ensure all fields have values.`,
      };
    }

    const columns = smartParseLine(line);

    // Extract symbol and weight based on column mapping or default order
    let symbol, weight;

    if (columnMapping) {
      // Custom header detected - use column indices
      const expectedMinColumns =
        Math.max(columnMapping.symbolIndex, columnMapping.weightIndex) + 1;
      if (columns.length < expectedMinColumns) {
        return {
          isValid: false,
          message: `Row ${
            i + 1
          }: Expected at least ${expectedMinColumns} columns based on header, found ${
            columns.length
          }.`,
        };
      }
      // Also check if there are extra columns (more than needed)
      // Portfolio should have exactly 2 columns (symbol and weight)
      if (columns.length > 2) {
        return {
          isValid: false,
          message: `Row ${i + 1}: Expected 2 columns, found ${columns.length}.`,
        };
      }
      symbol = columns[columnMapping.symbolIndex];
      weight = columns[columnMapping.weightIndex];
    } else {
      // No header or standard header - expect exactly 2 columns
      if (columns.length !== 2) {
        return {
          isValid: false,
          message: `Row ${i + 1}: Expected 2 columns, found ${columns.length}.`,
        };
      }
      [symbol, weight] = columns;
    }

    // Validate symbol (alphanumeric with "." and "-" separators)
    if (!SYMBOL_REGEX.test(symbol)) {
      return {
        isValid: false,
        message: `Row ${
          i + 1
        }: Symbol "${symbol}" should be alphanumeric with '.' and '-' separators.`,
      };
    }

    // Weight validation using processWeight for full format support
    const weightStr = weight.trim();
    // Pass decimal format from file-level detection to processWeight
    const weightResult = processWeight(weightStr, decimalFormatCheck.format);

    if (!weightResult.valid) {
      return {
        isValid: false,
        message: `Row ${i + 1}: ${weightResult.error}`,
      };
    }

    hasValidData = true;
  }

  if (!hasValidData) {
    return {
      isValid: false,
      message: "Portfolio must have at least one valid data row.",
    };
  }

  // Check gross exposure limit (must be <= 200)
  try {
    const normalized = normalizePortfolioContent(content);
    const metrics = calculatePortfolioMetrics(normalized);
    if (metrics.grossWeights > 200) {
      return {
        isValid: false,
        message: `Portfolio gross exposure (${metrics.grossWeights.toFixed(
          4,
        )}) exceeds the maximum allowed value of 200. Please reduce portfolio weights.`,
      };
    }
  } catch (error) {
    // If normalization/metrics calculation fails, allow validation to pass
    // (this should not happen if validation above passed, but handle gracefully)
    console.warn(
      "Error calculating portfolio metrics for exposure check:",
      error,
    );
  }

  return {
    isValid: true,
    message: "",
  };
};

/**
 * Validates if a collection requires portfolio validation
 * @param {Object} selectedCollection - The selected collection object
 * @returns {boolean} - True if collection requires portfolio validation
 */
export const isPortfolioCollection = (selectedCollection) => {
  return (
    selectedCollection && selectedCollection.is_portfolio_collection === true
  );
};

/**
 * Gets tooltip text for portfolio stamps without collection
 * Note: Portfolio stamps always require a collection (checkbox is disabled and always checked)
 * @param {Object|null} selectedOptionCollection - The selected collection
 * @param {boolean} activateSelectCollection - Whether collection selection is active (always true for portfolio)
 * @returns {string|null} - Tooltip text or null if no tooltip needed
 */
export const getPortfolioTooltipText = (
  selectedOptionCollection,
  activateSelectCollection,
) => {
  if (!selectedOptionCollection && activateSelectCollection) {
    return "Please select a collection for your portfolio stamp. Portfolio stamps must belong to a collection.";
  }
  return null;
};

/**
 * Calculates portfolio metrics from normalized CSV content
 * @param {string} normalizedContent - Normalized portfolio content (sym,wt format)
 * @returns {Object} Metrics object with grossWeights, netWeights, totalSymbols, hasDuplicates
 */
export const calculatePortfolioMetrics = (normalizedContent) => {
  if (!normalizedContent || !normalizedContent.trim()) {
    return {
      grossWeights: 0,
      netWeights: 0,
      totalSymbols: 0,
      hasDuplicates: false,
    };
  }

  const rows = normalizedContent
    .trim()
    .split("\n")
    .slice(1) // Skip header row
    .filter((row) => row.trim());

  if (rows.length === 0) {
    return {
      grossWeights: 0,
      netWeights: 0,
      totalSymbols: 0,
      hasDuplicates: false,
    };
  }

  const symbols = [];
  const weights = [];

  rows.forEach((row) => {
    const [symbol, weightStr] = row.split(",").map((col) => col.trim());
    if (symbol && weightStr) {
      symbols.push(symbol.toLowerCase()); // Case-insensitive for duplicate detection

      // Use processWeight for consistency (though normalized CSV should already be in standard format)
      const weightResult = processWeight(weightStr);
      if (weightResult.valid) {
        weights.push(weightResult.value);
      }
    }
  });

  const uniqueSymbols = new Set(symbols);
  const hasDuplicates = uniqueSymbols.size < symbols.length;

  const grossWeights = weights.reduce((sum, w) => sum + Math.abs(w), 0);
  const netWeights = weights.reduce((sum, w) => sum + w, 0);

  return {
    grossWeights,
    netWeights,
    totalSymbols: uniqueSymbols.size,
    hasDuplicates,
  };
};

/**
 * Generates warning messages based on portfolio metrics
 * @param {Object} metrics - Portfolio metrics from calculatePortfolioMetrics
 * @returns {Array<string>} Array of warning messages
 */
export const getPortfolioWarnings = (metrics) => {
  const warnings = [];

  if (metrics.grossWeights > 10) {
    warnings.push(
      "⚠️ WARNING: Portfolio with gross exposure > 10, may contain an error.",
    );
  }

  if (metrics.grossWeights < 0.1) {
    warnings.push(
      "⚠️ WARNING: Portfolio with gross exposure < 0.1 may be incomplete.",
    );
  }

  if (metrics.netWeights < -2) {
    warnings.push(
      "⚠️ WARNING: Highly levered short portfolio or possible net exposure error.",
    );
  }

  if (metrics.netWeights > 4) {
    warnings.push(
      "⚠️ WARNING: Highly levered net-long portfolio or possible net exposure error.",
    );
  }

  if (metrics.totalSymbols > 10000) {
    warnings.push("⚠️ WARNING: Portfolio contains more than 10,000 symbols.");
  }

  if (metrics.hasDuplicates) {
    warnings.push(
      "⚠️ WARNING: Duplicate symbols detected; weights will be summed across duplicates.",
    );
  }

  return warnings;
};
